import { useEffect, useRef, type MouseEvent } from 'react';

export const EmployerHero = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const pixelRatio = window.devicePixelRatio || 1;
    let animationFrame = 0;
    let width = 0;
    let height = 0;

    type Node = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      pulse: number;
      highlighted: boolean;
    };

    const nodes: Node[] = [];

    const resizeCanvas = () => {
      width = canvas.width = canvas.offsetWidth * pixelRatio;
      height = canvas.height = canvas.offsetHeight * pixelRatio;
    };

    resizeCanvas();
    const nodeCount = Math.min(70, Math.floor((canvas.offsetWidth * canvas.offsetHeight) / 16000));
    for (let i = 0; i < nodeCount; i += 1) {
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25 * pixelRatio,
        vy: (Math.random() - 0.5) * 0.25 * pixelRatio,
        radius: (Math.random() * 1.6 + 0.8) * pixelRatio,
        pulse: Math.random() * Math.PI * 2,
        highlighted: Math.random() < 0.12,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const first = nodes[i];
          const second = nodes[j];
          const dx = first.x - second.x;
          const dy = first.y - second.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const maximumDistance = 150 * pixelRatio;

          if (distance < maximumDistance) {
            const opacity = (1 - distance / maximumDistance) * 0.14;
            ctx.beginPath();
            ctx.moveTo(first.x, first.y);
            ctx.lineTo(second.x, second.y);
            ctx.strokeStyle = `rgba(11, 92, 34, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      nodes.forEach((node) => {
        if (!reduceMotion) {
          node.x += node.vx;
          node.y += node.vy;
          if (node.x < 0 || node.x > width) node.vx *= -1;
          if (node.y < 0 || node.y > height) node.vy *= -1;
          node.pulse += 0.02;
        }

        const glow = node.highlighted ? (Math.sin(node.pulse) + 1) / 2 : 0.15;
        ctx.beginPath();
        ctx.arc(
          node.x,
          node.y,
          node.radius + (node.highlighted ? glow * 2 * pixelRatio : 0),
          0,
          Math.PI * 2,
        );
        ctx.fillStyle = node.highlighted
          ? `rgba(28, 170, 60, ${0.55 + glow * 0.45})`
          : 'rgba(20, 21, 15, 0.22)';
        ctx.fill();
      });

      if (!reduceMotion) animationFrame = requestAnimationFrame(draw);
    };

    draw();

    window.addEventListener('resize', resizeCanvas);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  const scrollToHiringForm = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById('start-hiring')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <section className="qh-employer-hero">
      {/* Animated AI network */}
      <canvas ref={canvasRef} id="matchCanvas" className="qh-hero-canvas" aria-hidden="true" />

      <div className="qh-hero-inner">
        {/* Eyebrow */}
        <div className="qh-hero-eyebrow">
          <span className="qh-hero-dot" />
          THE AI-POWERED RECRUITING PLATFORM — NOT A STAFFING AGENCY
        </div>

        {/* Heading */}
        <h1>
          Recruiters working
          <br />
          at <em>10x speed.</em>
          <br />
          You only see who's hired.
        </h1>

        {/* Description */}
        <p className="qh-hero-lede">
          Every shortlist we send is powered by an AI matching engine we built in-house — paired
          with real recruiters who run outreach, screening, and close. It's software that finds
          candidates faster than a manual search, backed by a person on the phone.
        </p>

        {/* CTA */}
        <div className="qh-hero-cta-row">
          <a href="#start-hiring" className="qh-hero-primary" onClick={scrollToHiringForm}>
            Send us your next req →
          </a>

          <a href="#not-ats" className="qh-hero-ghost">
            How we compare to traditional agencies
          </a>
        </div>

        {/* Stats - part of hero, exactly like sample */}
        <div className="qh-stat-strip">
          <div className="qh-stat">
            <div className="qh-stat-number">
              5M<span>+</span>
            </div>

            <div className="qh-stat-label">Profiles our recruiters draw on</div>
          </div>

          <div className="qh-stat">
            <div className="qh-stat-number">
              100K<span>+</span>
            </div>

            <div className="qh-stat-label">Fresh CVs, every month</div>
          </div>

          <div className="qh-stat">
            <div className="qh-stat-number">
              10<span>x</span>
            </div>

            <div className="qh-stat-label">Recruiter throughput, internally</div>
          </div>

          <div className="qh-stat">
            <div className="qh-stat-number">
              24<span>/7</span>
            </div>

            <div className="qh-stat-label">Sourcing, never idle</div>
          </div>
        </div>
      </div>
    </section>
  );
};
